import { FreelancerProfile } from './api/freelancers';
import { ProjectWithDetails } from './api/projects';
import { ProfileData } from './api/profile';

export interface DynamicSEOData {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType: string;
  structuredData?: object;
  noIndex?: boolean;
}

/**
 * Generate SEO data for freelancer/agency profiles
 */
export const generateProfileSEO = (profile: FreelancerProfile | ProfileData, language: string = 'bg'): DynamicSEOData => {
  const isAgency = 'is_agency' in profile ? profile.is_agency : profile.role === 'AGENCY';
  const name = profile.full_name || ('company_name' in profile ? profile.company_name : profile.company) || 'Unknown';
  const skills = profile.skills?.slice(0, 5).join(', ') || '';
  const location = profile.location || '';
  
  const baseUrl = 'https://workio.bg';
  const profileUrl = `/freelancer/${profile.id}`;
  
  if (language === 'bg') {
    const title = isAgency 
      ? `${name} - IT Агенция | Workio`
      : `${name} - IT Специалист | Workio`;
    
    const description = isAgency
      ? `Професионална IT агенция ${name}${location ? ` от ${location}` : ''}. Специализирана в ${skills}. Намерете най-добрите IT решения за вашия бизнес.`
      : `IT специалист ${name}${location ? ` от ${location}` : ''}. Експерт в ${skills}. Намерете професионалист за вашия IT проект.`;
    
    const keywords = isAgency
      ? `IT агенция, ${name}, ${skills}, ${location}, IT услуги, софтуерно инженерство, веб разработка, мобилни приложения, Workio`
      : `IT специалист, ${name}, ${skills}, ${location}, програмист, дизайнер, фрийлансър, IT проект, Workio`;
    
    return {
      title,
      description: description.length > 160 ? description.substring(0, 157) + '...' : description,
      keywords,
      canonicalUrl: profileUrl,
      ogType: 'profile',
      structuredData: {
        "@context": "https://schema.org",
        "@type": isAgency ? "Organization" : "Person",
        "name": name,
        "url": `${baseUrl}${profileUrl}`,
        "description": description,
        "address": location ? {
          "@type": "PostalAddress",
          "addressLocality": location
        } : undefined,
        "knowsAbout": profile.skills || [],
        "jobTitle": isAgency ? "IT Agency" : "IT Specialist",
        "worksFor": isAgency ? undefined : {
          "@type": "Organization",
          "name": "Workio"
        }
      }
    };
  } else {
    const title = isAgency 
      ? `${name} - IT Agency | Workio`
      : `${name} - IT Specialist | Workio`;
    
    const description = isAgency
      ? `Professional IT agency ${name}${location ? ` from ${location}` : ''}. Specialized in ${skills}. Find the best IT solutions for your business.`
      : `IT specialist ${name}${location ? ` from ${location}` : ''}. Expert in ${skills}. Find a professional for your IT project.`;
    
    const keywords = isAgency
      ? `IT agency, ${name}, ${skills}, ${location}, IT services, software engineering, web development, mobile apps, Workio`
      : `IT specialist, ${name}, ${skills}, ${location}, programmer, designer, freelancer, IT project, Workio`;
    
    return {
      title,
      description: description.length > 160 ? description.substring(0, 157) + '...' : description,
      keywords,
      canonicalUrl: profileUrl,
      ogType: 'profile',
      structuredData: {
        "@context": "https://schema.org",
        "@type": isAgency ? "Organization" : "Person",
        "name": name,
        "url": `${baseUrl}${profileUrl}`,
        "description": description,
        "address": location ? {
          "@type": "PostalAddress",
          "addressLocality": location
        } : undefined,
        "knowsAbout": profile.skills || [],
        "jobTitle": isAgency ? "IT Agency" : "IT Specialist",
        "worksFor": isAgency ? undefined : {
          "@type": "Organization",
          "name": "Workio"
        }
      }
    };
  }
};

/**
 * Generate SEO data for project detail pages
 */
export const generateProjectSEO = (project: ProjectWithDetails, language: string = 'bg'): DynamicSEOData => {
  const title = project.title || 'Untitled Project';
  const description = project.description || '';
  const budget = project.budget_min ? `€${project.budget_min.toLocaleString()}` : '';
  const category = project.category?.name || '';
  const skills = project.skills?.slice(0, 5).join(', ') || '';
  const client = project.client?.full_name || 'Anonymous Client';
  
  const baseUrl = 'https://workio.bg';
  const projectUrl = `/project/${project.id}`;
  
  if (language === 'bg') {
    const seoTitle = `${title} - IT Проект | Workio`;
    const seoDescription = `${description.substring(0, 120)}${description.length > 120 ? '...' : ''} ${budget ? `Бюджет: ${budget}` : ''} ${category ? `Категория: ${category}` : ''}`;
    const seoKeywords = `IT проект, ${title}, ${category}, ${skills}, ${budget ? `бюджет ${budget}` : ''}, фрийлансър, програмист, Workio`;
    
    return {
      title: seoTitle,
      description: seoDescription.length > 160 ? seoDescription.substring(0, 157) + '...' : seoDescription,
      keywords: seoKeywords,
      canonicalUrl: projectUrl,
      ogType: 'article',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": title,
        "description": description,
        "url": `${baseUrl}${projectUrl}`,
        "datePosted": project.created_at,
        "validThrough": project.deadline,
        "hiringOrganization": {
          "@type": "Organization",
          "name": client
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "BG"
          }
        },
        "employmentType": "CONTRACTOR",
        "baseSalary": project.budget_min ? {
          "@type": "MonetaryAmount",
          "currency": "EUR",
          "value": {
            "@type": "QuantitativeValue",
            "minValue": project.budget_min,
            "maxValue": project.budget_max || project.budget_min
          }
        } : undefined,
        "skills": project.skills || [],
        "industry": category
      }
    };
  } else {
    const seoTitle = `${title} - IT Project | Workio`;
    const seoDescription = `${description.substring(0, 120)}${description.length > 120 ? '...' : ''} ${budget ? `Budget: ${budget}` : ''} ${category ? `Category: ${category}` : ''}`;
    const seoKeywords = `IT project, ${title}, ${category}, ${skills}, ${budget ? `budget ${budget}` : ''}, freelancer, programmer, Workio`;
    
    return {
      title: seoTitle,
      description: seoDescription.length > 160 ? seoDescription.substring(0, 157) + '...' : seoDescription,
      keywords: seoKeywords,
      canonicalUrl: projectUrl,
      ogType: 'article',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": title,
        "description": description,
        "url": `${baseUrl}${projectUrl}`,
        "datePosted": project.created_at,
        "validThrough": project.deadline,
        "hiringOrganization": {
          "@type": "Organization",
          "name": client
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "BG"
          }
        },
        "employmentType": "CONTRACTOR",
        "baseSalary": project.budget_min ? {
          "@type": "MonetaryAmount",
          "currency": "EUR",
          "value": {
            "@type": "QuantitativeValue",
            "minValue": project.budget_min,
            "maxValue": project.budget_max || project.budget_min
          }
        } : undefined,
        "skills": project.skills || [],
        "industry": category
      }
    };
  }
};

/**
 * Generate SEO data for service detail pages
 */
export const generateServiceSEO = (service: any, language: string = 'bg'): DynamicSEOData => {
  const title = service.title || 'Untitled Service';
  const description = service.description || '';
  const price = service.price ? `€${service.price.toLocaleString()}` : '';
  const category = service.category?.name || '';
  const skills = service.skills?.slice(0, 5).join(', ') || '';
  const provider = service.provider?.full_name || 'Unknown Provider';
  
  const baseUrl = 'https://workio.bg';
  const serviceUrl = `/service/${service.id}`;
  
  if (language === 'bg') {
    const seoTitle = `${title} - IT Услуга | Workio`;
    const seoDescription = `${description.substring(0, 120)}${description.length > 120 ? '...' : ''} ${price ? `Цена: ${price}` : ''} ${category ? `Категория: ${category}` : ''}`;
    const seoKeywords = `IT услуга, ${title}, ${category}, ${skills}, ${price ? `цена ${price}` : ''}, фрийлансър, програмист, Workio`;
    
    return {
      title: seoTitle,
      description: seoDescription.length > 160 ? seoDescription.substring(0, 157) + '...' : seoDescription,
      keywords: seoKeywords,
      canonicalUrl: serviceUrl,
      ogType: 'product',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": title,
        "description": description,
        "url": `${baseUrl}${serviceUrl}`,
        "provider": {
          "@type": "Person",
          "name": provider
        },
        "offers": service.price ? {
          "@type": "Offer",
          "price": service.price,
          "priceCurrency": "EUR"
        } : undefined,
        "category": category,
        "serviceType": "IT Service",
        "areaServed": {
          "@type": "Country",
          "name": "Bulgaria"
        }
      }
    };
  } else {
    const seoTitle = `${title} - IT Service | Workio`;
    const seoDescription = `${description.substring(0, 120)}${description.length > 120 ? '...' : ''} ${price ? `Price: ${price}` : ''} ${category ? `Category: ${category}` : ''}`;
    const seoKeywords = `IT service, ${title}, ${category}, ${skills}, ${price ? `price ${price}` : ''}, freelancer, programmer, Workio`;
    
    return {
      title: seoTitle,
      description: seoDescription.length > 160 ? seoDescription.substring(0, 157) + '...' : seoDescription,
      keywords: seoKeywords,
      canonicalUrl: serviceUrl,
      ogType: 'product',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": title,
        "description": description,
        "url": `${baseUrl}${serviceUrl}`,
        "provider": {
          "@type": "Person",
          "name": provider
        },
        "offers": service.price ? {
          "@type": "Offer",
          "price": service.price,
          "priceCurrency": "EUR"
        } : undefined,
        "category": category,
        "serviceType": "IT Service",
        "areaServed": {
          "@type": "Country",
          "name": "Bulgaria"
        }
      }
    };
  }
};
